import { CancelTokenSource } from 'axios';

export interface FileWithExtraProps {
  name: string;
  size: number;
  lastModified: number;
  slice: File['slice'];
  arrayBuffer: File['arrayBuffer'];
  stream: File['stream'];
  text: File['text'];
  webkitRelativePath: string;
  type: string;
  uid?: string;
  percent?: number;
}

export enum FileTrackerStageEnum {
  FILE_RECEIVED = 'FILE RECEIVED',
  FILE_IS_RECEIVED = 'FILE IS RECEIVED',
  MALWARE_SCAN_INPROGRESS = 'MALWARE SCAN IN PROGRESS',
  MALWARE_SCAN_COMPLETED = 'MALWARE SCAN SUCCESS',
  MALWARE_SCAN_FAILED = 'MALWARE SCAN FAILED',
  FILE_SAMPLING_IN_PROGRESS = 'FILE SAMPLING IN PROGRESS',
  FILE_SAMPLING_INPROGRESS = 'FILE SAMPLING INPROGRESS', // added to support some old sample files
  FILE_SAMPLING_COMPLETED = 'FILE SAMPLING COMPLETED',
  FILE_SAMPLING_FAILED = 'FILE SAMPLING FAILED',
  FILE_IS_AVAILABLE = 'FILE IS AVAILABLE',
}

export const completedStates = [
  FileTrackerStageEnum.FILE_SAMPLING_COMPLETED as string,
  FileTrackerStageEnum.FILE_IS_AVAILABLE,
  FileTrackerStageEnum.FILE_RECEIVED,
  FileTrackerStageEnum.MALWARE_SCAN_COMPLETED,
  FileTrackerStageEnum.FILE_IS_RECEIVED,
];
export const inProgStates = [
  FileTrackerStageEnum.MALWARE_SCAN_INPROGRESS as string,
  FileTrackerStageEnum.FILE_SAMPLING_IN_PROGRESS,
  FileTrackerStageEnum.FILE_SAMPLING_INPROGRESS,
];
export const failedStates = [
  FileTrackerStageEnum.MALWARE_SCAN_FAILED as string,
  FileTrackerStageEnum.FILE_SAMPLING_FAILED,
];

export enum FileStageStatus {
  In_Progress = 'In Progress',
  Failed = 'Failed',
  Completed = 'Completed',
  File_is_Available = 'File is Available',
  File_Received = 'File Received',
}

export enum LayerStatusEnum {
  success = 'success',
  error = 'error',
  inprogress = 'inprogress',
  waiting = 'waiting',
}

export enum FileTrackerTabEnum {
  received = 'received',
  inProgress = 'inProgress',
  scanCompleted = 'scanCompleted',
  sampling = 'sampling',
  available = 'available',
  failed = 'failed',
}
export enum FileUploadLayers {
  AscendDataCuration = 'AscendDataCuration',
  FileService = 'FileService',
  Sts = 'STS',
}
export const LayersProcessMap: Record<string, string> = {
  [FileUploadLayers.AscendDataCuration]: 'Malware Scan',
  [FileUploadLayers.FileService]: 'File Sampling',
  [FileUploadLayers.Sts]: 'STS',
};

export type FileTrackerListInput = {
  clientIdentifier: string;
  product: string;
  intentType?: string[]; // Sync up on Anirudh on how we are passing it on file service api.
  onlyInProgress: boolean;
  limitBy?: number;
};

export type FileTrackerListFileProps = {
  fileId: string;
  fileName: string;
  client?: string;
  product?: string;
  intentType: string;
  layer: string; // STS, Landing Zone, File Service
  layerUpdateTime?: number; // epoch milli
  stage: string; // status in BE: File Received, Malware Scan
  stageUpdateTime?: number; // epoch milli
  fileSize: number; // in bytes
  recordCount?: number;
  uploadSource?: string; // S3, STS, UI etc for UI the consumer will put a tag saying the source is UI.
  uploadedBy?: string; // uplaoded By could be a s3 tag put by consumer
};

export type FileUploadStatsDetails = {
  available: number;
  sampling: number;
  malwareScan: number;
  received: number;
};

export type ChartDataProps =
  | {
      day: string;
      Uploads: number;
    }[]
  | {
      month: string;
      Uploads: number;
    }[];

export type FileUploadStatsProps = {
  details: FileUploadStatsDetails;
  percentagedetails: FileUploadStatsDetails;
  chartData: ChartDataProps;
};

export type FileTrackerListProps = {
  clientIdentifier: string;
  product: string;
  files: FileTrackerListFileProps[];
};

export type FileTrackerDetailRequest = {
  fileId: string;
};

export type FileTrackerLayerStatus = {
  name: string; // status in BE: File Received, Malware Scan
  updateTime?: number; // epoch milli
};

export type FileTrackerLayer = {
  name: string; // STS, Landing Zone, File Service
  updateTime?: number; // epoch milli,
  statuses: FileTrackerLayerStatus[];
};

export type FileTrackerDetailProps = {
  fileId: string;
  fileName: string;
  intentType: string;
  fileSize: number; // in bytes
  recordCount?: number;
  uploadSource?: string; // S3, STS, UI etc for UI the consumer will put a tag saying the source is UI.
  uploadedBy?: string; // uplaoded By could be a s3 tag put by consumer
  layers: FileTrackerLayer[];
};

export type FileTrackerFileStats = {
  avgFileAvailableTime: number;
  avgFileSize: number;
  largeFiles: number;
  totalFiles: number;
};

export type FileTrackerMetrics = {
  stats: FileTrackerFileStats;
  recentFiles: (FileTrackerListFileProps | undefined)[];
  fileUploadStats: FileUploadStatsProps;
};

export interface InputFileUploadStatus {
  uniqueId: string;
  fileName: string;
  layer: string;
  keyPrefix: string;
  timeStamp: number;
  user: string;
  status: string;
}

export interface LayerStagesProps {
  name: string;
  key: string[];
}

export interface FileUploadStatusDetails {
  name: string;
  updateTime: number;
}

export interface FileTrackerConfigProps {
  isFileListRequired?: boolean; // making this false will disable file-upload also
  isMetricesRequired?: {
    fileStats: boolean;
    fileUploadTrend: boolean;
  };
  isRecentFilesRequired?: boolean;
  isUploadRequired?: boolean;
  isQuickViewRequired?: boolean;
  subCode?: string;
  availbleSubCodes?: string[] | '*';
}

export interface FileTrackerDataProps {
  getFileList?: (fileLimit?: number) => Promise<FileTrackerListProps | Error>;
  getMetrics?: (monthValue: number) => Promise<FileTrackerMetrics | Error>;
  getFileStatus?: (
    val: FileTrackerListFileProps,
  ) => Promise<FileTrackerDetailProps | undefined>;
  getUploadStatus?: (
    formData: FormData,
    cancelTokenSource: CancelTokenSource,
    addedFile: FileWithExtraProps,
    setFilesUploadPercentage: (
      value: React.SetStateAction<FileWithExtraProps[]>,
    ) => void,
  ) => void;
  exitFileTracker?: () => void;
  getClientCode?: (value: React.SetStateAction<string>) => void;
}

export interface isFileLoadingProps {
  fileList: boolean;
  recentFiles: boolean;
}
